import type { RemixPhase } from '@atlassian/malleable-ui-components/state/machine';

/**
 * Renderer-owned lifecycle vocabulary shared by the migration adapters.
 *
 * This vocabulary describes renderer behavior rather than exposing the raw
 * machine phase union. `submitting` is the renderer projection of the machine's
 * `inserting` phase; the legacy adapter does not fabricate that state.
 */
export type RendererRemixLifecyclePhase =
	| 'inactive'
	| 'thinking'
	| 'ready'
	| 'previewing'
	| 'submitting'
	| 'error'
	| 'aborted';

export type LegacyRendererRemixSignals = {
	isThinking: boolean;
	isReady: boolean;
	isPreviewVisible: boolean;
	isError: boolean;
	isAborted: boolean;
	isCancelled: boolean;
};

/**
 * Normalize the existing renderer signals without changing their authority or
 * executing product behavior. During shadow this is suitable for comparison;
 * CCCEMAU-3480 can later select between legacy and machine-backed reads at one
 * renderer-owned seam.
 */
export const deriveLegacyRendererRemixPhase = ({
	isThinking,
	isReady,
	isPreviewVisible,
	isError,
	isAborted,
	isCancelled,
}: LegacyRendererRemixSignals): RendererRemixLifecyclePhase => {
	if (isError) {
		return 'error';
	}

	if (isAborted || isCancelled) {
		return 'aborted';
	}

	if (isPreviewVisible) {
		return 'previewing';
	}

	if (isReady) {
		return 'ready';
	}

	if (isThinking) {
		return 'thinking';
	}

	return 'inactive';
};

const machineToRendererPhase = {
	thinking: 'thinking',
	ready: 'ready',
	previewing: 'previewing',
	inserting: 'submitting',
	error: 'error',
	aborted: 'aborted',
} as const satisfies Record<RemixPhase, RendererRemixLifecyclePhase>;

export const deriveMachineRendererRemixPhase = (
	phase: RemixPhase | undefined,
): RendererRemixLifecyclePhase =>
	phase === undefined ? 'inactive' : machineToRendererPhase[phase];
